import { Container, Graphics, Sprite, Text } from 'pixi.js';
import type {
  CharacterDefinition,
  CharacterExpressionDefinition,
  CharacterPosition,
  ScenarioCharacterCue
} from './types';
import charactersData from '../data/characters.json';
import type { AssetLoader, ImageAssetResult } from '../systems/AssetLoader';
import { UI_COLORS, UI_FONT_FAMILY } from '../ui';

export interface CharacterManagerOptions {
  assetLoader: AssetLoader;
  width?: number;
  height?: number;
  characters?: readonly CharacterDefinition[];
}

export interface CharacterDisplayState {
  id: string;
  displayName: string;
  fullName: string;
  expression: string;
  position: CharacterPosition;
  offsetY: number;
  frame: 'none' | 'photo';
  assetId: string;
  fileName: string;
  assetPath: string;
  placeholder: boolean;
}

export interface CharacterDisplayDebugState extends CharacterDisplayState {
  displayIndex: number;
  targetX: number;
  targetY: number;
  x: number;
  y: number;
  alpha: number;
  exiting: boolean;
}

export interface CharacterManagerDebugState {
  characterCount: number;
  characters: CharacterDisplayDebugState[];
}

interface CharacterLayer {
  container: Container;
  state: CharacterDisplayState;
  animation: CharacterAnimation | null;
  exiting: boolean;
  introStyle: CharacterIntroStyle;
  targetX: number;
  targetY: number;
}

interface CharacterAnimation {
  kind: 'enter' | 'exit' | 'expressionIn' | 'expressionOut' | 'move';
  elapsedMs: number;
  durationMs: number;
  fromX: number;
  toX: number;
  fromY: number;
  toY: number;
  fromAlpha: number;
  toAlpha: number;
  fromScale: number;
  toScale: number;
}

type CharacterIntroStyle = 'enter' | 'expression';

interface CharacterAssetReference {
  id: string;
  fileName: string;
  assetPath: string;
  path?: string;
}

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;
const CHARACTER_BASELINE_OFFSET = -230;
const CHARACTER_TARGET_HEIGHT = 790;
const CHARACTER_TARGET_WIDTH = 540;
const CHARACTER_ENTER_DURATION_MS = 260;
const CHARACTER_EXIT_DURATION_MS = 180;
const CHARACTER_EXPRESSION_CROSSFADE_MS = 140;
const CHARACTER_MOVE_DURATION_MS = 220;
const CHARACTER_SLIDE_OFFSET = 48;
const CHARACTERS = (charactersData as { characters: CharacterDefinition[] }).characters;
const DISPLAY_DESTROY_OPTIONS = { children: true, context: true } as const;
const CHARACTER_ASSET_PREFIX = 'assets/images/characters/';
const POSITION_RATIO: Record<CharacterPosition, number> = {
  left: 0.273,
  center: 0.5,
  right: 0.727
};

export class CharacterManager {
  public readonly container = new Container();

  private readonly characterById: Map<string, CharacterDefinition>;
  private readonly assetLoader: AssetLoader;
  private readonly imageCache = new Map<string, Promise<ImageAssetResult>>();
  private width: number;
  private height: number;
  private requestToken = 0;
  private destroyed = false;
  private layers: CharacterLayer[] = [];

  public constructor(options: CharacterManagerOptions) {
    this.assetLoader = options.assetLoader;
    this.width = options.width ?? DEFAULT_WIDTH;
    this.height = options.height ?? DEFAULT_HEIGHT;
    this.characterById = new Map(
      (options.characters ?? CHARACTERS).map((character) => [character.id, character])
    );
  }

  public async setCharacters(cues: readonly ScenarioCharacterCue[] | undefined): Promise<void> {
    if (this.destroyed) {
      return;
    }

    const token = ++this.requestToken;
    const visibleCues = (cues ?? []).filter((cue) => cue.visible !== false).map(cloneCue);
    const existingLayers = new Map(
      this.layers
        .filter((layer) => !layer.exiting)
        .map((layer) => [createLayerKey(layer.state.id, layer.state.expression), layer])
    );
    const existingLayersByCharacterId = new Map(
      this.layers.filter((layer) => !layer.exiting).map((layer) => [layer.state.id, layer])
    );
    const usedLayers = new Set<CharacterLayer>();
    const expressionReplacementIds = new Set<string>();
    const creationRequests: Array<{
      cue: ScenarioCharacterCue;
      layer: Promise<CharacterLayer>;
      introStyle: CharacterIntroStyle;
    }> = [];

    for (const cue of visibleCues) {
      const definition = this.characterById.get(cue.id);
      const expression = resolveExpression(definition, cue.expression);
      const existingLayer = existingLayers.get(createLayerKey(cue.id, expression.id));
      if (existingLayer) {
        usedLayers.add(existingLayer);
        continue;
      }

      const existingCharacterLayer = existingLayersByCharacterId.get(cue.id);
      if (existingCharacterLayer) {
        expressionReplacementIds.add(cue.id);
      }

      creationRequests.push({
        cue,
        layer: this.createLayer(cue),
        introStyle: existingCharacterLayer ? 'expression' : 'enter'
      });
    }

    const createdLayers = await Promise.all(creationRequests.map((request) => request.layer));
    if (this.destroyed || token !== this.requestToken) {
      destroyLayers(createdLayers);
      return;
    }

    const nextActiveLayers: CharacterLayer[] = [];
    let createdLayerIndex = 0;
    for (const cue of visibleCues) {
      const definition = this.characterById.get(cue.id);
      const expression = resolveExpression(definition, cue.expression);
      const existingLayer = existingLayers.get(createLayerKey(cue.id, expression.id));
      if (existingLayer && usedLayers.has(existingLayer)) {
        existingLayer.exiting = false;
        existingLayer.state = {
          ...existingLayer.state,
          position: cue.position,
          offsetY: cue.offsetY ?? 0,
          frame: cue.frame ?? 'none'
        };
        nextActiveLayers.push(existingLayer);
        continue;
      }

      const createdLayer = createdLayers[createdLayerIndex];
      const createdRequest = creationRequests[createdLayerIndex];
      createdLayerIndex += 1;
      if (createdLayer) {
        createdLayer.introStyle = createdRequest?.introStyle ?? 'enter';
        nextActiveLayers.push(createdLayer);
      }
    }

    const outgoingLayers = this.layers.filter((layer) => !nextActiveLayers.includes(layer));
    for (const layer of outgoingLayers) {
      if (!layer.exiting) {
        this.startExitAnimation(
          layer,
          expressionReplacementIds.has(layer.state.id) ? 'expression' : 'exit'
        );
      }
    }

    this.layers = [...outgoingLayers, ...nextActiveLayers];
    this.syncDisplayOrder();
    this.layout();
  }

  public resize(width: number, height: number): void {
    this.width = Math.max(1, Math.round(width));
    this.height = Math.max(1, Math.round(height));
    this.layout();
  }

  public update(deltaMs: number): void {
    if (this.destroyed) {
      return;
    }

    const safeDeltaMs = Math.max(0, deltaMs);
    for (const layer of this.layers) {
      this.updateLayerAnimation(layer, safeDeltaMs);
    }

    const beforeCount = this.layers.length;
    this.layers = this.layers.filter((layer) => {
      if (!layer.exiting || layer.animation) {
        return true;
      }

      layer.container.parent?.removeChild(layer.container);
      layer.container.destroy(DISPLAY_DESTROY_OPTIONS);
      return false;
    });

    if (this.layers.length !== beforeCount) {
      this.syncDisplayOrder();
    }
  }

  public getDebugState(): CharacterManagerDebugState {
    return {
      characterCount: this.layers.length,
      characters: this.layers.map((layer, displayIndex) => ({
        ...layer.state,
        displayIndex,
        targetX: layer.targetX,
        targetY: layer.targetY,
        x: Math.round(layer.container.x),
        y: Math.round(layer.container.y),
        alpha: layer.container.alpha,
        exiting: layer.exiting
      }))
    };
  }

  public destroy(): void {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;
    this.requestToken += 1;
    destroyLayers(this.layers);
    this.layers = [];
    this.container.parent?.removeChild(this.container);
    this.container.destroy(DISPLAY_DESTROY_OPTIONS);
  }

  private async createLayer(cue: ScenarioCharacterCue): Promise<CharacterLayer> {
    const definition = this.characterById.get(cue.id);
    const expression = resolveExpression(definition, cue.expression);
    let asset = getCharacterAssetReference(cue.id, expression);
    let image = await this.loadCharacterImage(asset);
    if (image.placeholder) {
      const fallback = await this.loadDefaultCharacterImage(cue.id, definition, expression);
      if (fallback) {
        asset = fallback.asset;
        image = fallback.image;
      }
    }
    const container = new Container();
    const state: CharacterDisplayState = {
      id: cue.id,
      displayName: definition?.displayName ?? cue.id,
      fullName: definition?.fullName ?? definition?.displayName ?? cue.id,
      expression: expression.id,
      position: cue.position,
      offsetY: cue.offsetY ?? 0,
      frame: cue.frame ?? 'none',
      assetId: asset.id,
      fileName: asset.fileName,
      assetPath: asset.assetPath,
      placeholder: image.placeholder
    };

    if (image.placeholder) {
      container.addChild(this.createPlaceholder(cue, definition, expression));
    } else {
      container.addChild(
        cue.frame === 'photo' ? this.createPhotoSprite(image) : this.createSprite(image)
      );
    }

    container.alpha = 0;
    return {
      container,
      state,
      animation: null,
      exiting: false,
      introStyle: 'enter',
      targetX: 0,
      targetY: 0
    };
  }

  private loadCharacterImage(asset: CharacterAssetReference): Promise<ImageAssetResult> {
    const cacheKey = asset.path ?? asset.id;
    let promise = this.imageCache.get(cacheKey);
    if (!promise) {
      promise = this.assetLoader.loadImage({
        id: asset.id,
        kind: 'character',
        path: asset.path
      });
      this.imageCache.set(cacheKey, promise);
    }

    return promise;
  }

  private async loadDefaultCharacterImage(
    characterId: string,
    definition: CharacterDefinition | undefined,
    requestedExpression: CharacterExpressionDefinition
  ): Promise<{ asset: CharacterAssetReference; image: ImageAssetResult } | null> {
    if (!definition || requestedExpression.id === definition.defaultExpression) {
      return null;
    }

    const defaultExpression = resolveExpression(definition, definition.defaultExpression);
    if (defaultExpression.id === requestedExpression.id) {
      return null;
    }

    const asset = getCharacterAssetReference(characterId, defaultExpression);
    const image = await this.loadCharacterImage(asset);
    return image.placeholder ? null : { asset, image };
  }

  private createSprite(image: ImageAssetResult): Container {
    const layer = new Container();
    const sprite = new Sprite(image.texture);
    sprite.anchor.set(0.5, 1);
    fitSprite(sprite, CHARACTER_TARGET_WIDTH, CHARACTER_TARGET_HEIGHT);
    sprite.position.set(0, 0);
    layer.addChild(sprite);
    return layer;
  }

  private createPhotoSprite(image: ImageAssetResult): Container {
    const layer = new Container();
    const frameWidth = 340;
    const frameHeight = 450;
    const innerMargin = 18;
    const captionHeight = 48;
    const innerX = -frameWidth / 2 + innerMargin;
    const innerY = -frameHeight + innerMargin;
    const innerWidth = frameWidth - innerMargin * 2;
    const innerHeight = frameHeight - innerMargin * 2 - captionHeight;

    const shadow = new Graphics();
    shadow.roundRect(-frameWidth / 2 + 10, -frameHeight + 12, frameWidth, frameHeight, 10).fill({
      color: UI_COLORS.black,
      alpha: 0.36
    });
    layer.addChild(shadow);

    const frame = new Graphics();
    frame.roundRect(-frameWidth / 2, -frameHeight, frameWidth, frameHeight, 10).fill({
      color: UI_COLORS.paperLight,
      alpha: 0.96
    });
    frame.roundRect(-frameWidth / 2, -frameHeight, frameWidth, frameHeight, 10).stroke({
      color: UI_COLORS.gold,
      width: 3,
      alpha: 0.92
    });
    frame
      .rect(innerX, innerY, innerWidth, innerHeight)
      .fill({ color: UI_COLORS.black, alpha: 0.9 });
    layer.addChild(frame);

    const sprite = new Sprite(image.texture);
    const scale = Math.max(
      innerWidth / Math.max(1, image.texture.width),
      innerHeight / Math.max(1, image.texture.height)
    );
    sprite.anchor.set(0.5, 1);
    sprite.scale.set(scale);
    sprite.position.set(0, innerY + innerHeight);

    const mask = new Graphics();
    mask.rect(innerX, innerY, innerWidth, innerHeight).fill(UI_COLORS.paperLight);
    sprite.mask = mask;
    layer.addChild(sprite);
    layer.addChild(mask);

    const label = new Text({
      text: 'MISSING',
      style: {
        fill: UI_COLORS.red,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 19,
        fontWeight: '900'
      }
    });
    label.anchor.set(0.5);
    label.position.set(0, -26);
    layer.addChild(label);

    const tape = new Graphics();
    tape.rect(-frameWidth / 2 + 22, -frameHeight + 18, 72, 16).fill({
      color: UI_COLORS.gold,
      alpha: 0.62
    });
    tape.rect(frameWidth / 2 - 94, -frameHeight + 18, 72, 16).fill({
      color: UI_COLORS.gold,
      alpha: 0.62
    });
    layer.addChild(tape);

    return layer;
  }

  private createPlaceholder(
    cue: ScenarioCharacterCue,
    definition: CharacterDefinition | undefined,
    expression: CharacterExpressionDefinition
  ): Container {
    const layer = new Container();
    const roleAccent = getRoleAccent(definition?.role);
    const body = new Graphics();
    body.roundRect(-108, -444, 216, 444, 22).fill({ color: UI_COLORS.black, alpha: 0.74 });
    body.roundRect(-106, -444, 212, 444, 22).stroke({ color: roleAccent, width: 3, alpha: 0.86 });
    body.circle(0, -518, 70).fill({ color: UI_COLORS.paperLight, alpha: 0.9 });
    body.circle(0, -518, 70).stroke({ color: roleAccent, width: 3, alpha: 0.8 });
    body.rect(-74, -426, 148, 52).fill({ color: roleAccent, alpha: 0.28 });
    layer.addChild(body);
    layer.addChild(this.createNamePlate(cue, definition, expression, true));
    return layer;
  }

  private createNamePlate(
    cue: ScenarioCharacterCue,
    definition: CharacterDefinition | undefined,
    expression: CharacterExpressionDefinition,
    placeholder: boolean
  ): Container {
    const layer = new Container();
    const nameText = new Text({
      text: definition?.displayName ?? cue.id,
      style: {
        fill: UI_COLORS.ink,
        fontFamily: UI_FONT_FAMILY,
        fontSize: placeholder ? 21 : 18,
        fontWeight: '900'
      }
    });
    nameText.anchor.set(0.5);
    nameText.position.set(0, placeholder ? -392 : -76);
    layer.addChild(nameText);

    const expressionText = new Text({
      text: expression.id,
      style: {
        fill: UI_COLORS.mutedInk,
        fontFamily: UI_FONT_FAMILY,
        fontSize: 14,
        fontWeight: '700'
      }
    });
    expressionText.anchor.set(0.5);
    expressionText.position.set(0, placeholder ? -360 : -52);
    layer.addChild(expressionText);
    return layer;
  }

  private layout(): void {
    const baseline = this.height - CHARACTER_BASELINE_OFFSET;
    for (const layer of this.layers) {
      const targetX = Math.round(this.width * POSITION_RATIO[layer.state.position]);
      const targetY = baseline + layer.state.offsetY;
      const previousTargetX = layer.targetX;
      const previousTargetY = layer.targetY;
      layer.targetX = targetX;
      layer.targetY = targetY;

      if (layer.exiting) {
        continue;
      }

      if (layer.container.alpha === 0 && !layer.animation) {
        this.startIntroAnimation(layer);
        continue;
      }

      if (
        !layer.animation &&
        (Math.abs(previousTargetX - targetX) > 0 || Math.abs(previousTargetY - targetY) > 0)
      ) {
        this.startMoveAnimation(layer);
        continue;
      }

      if (!layer.animation) {
        layer.container.position.set(targetX, targetY);
        layer.container.scale.set(1);
        layer.container.alpha = 1;
      } else if (layer.animation.kind !== 'exit') {
        layer.animation.toX = targetX;
        layer.animation.toY = targetY;
      }
    }
  }

  private syncDisplayOrder(): void {
    this.container.removeChildren();
    for (const layer of this.layers) {
      this.container.addChild(layer.container);
    }
  }

  private startIntroAnimation(layer: CharacterLayer): void {
    if (layer.introStyle === 'expression') {
      this.startExpressionInAnimation(layer);
      return;
    }

    const slideOffset = getEnterSlideOffset(layer.state.position);
    layer.container.position.set(layer.targetX + slideOffset, layer.targetY + 18);
    layer.container.scale.set(0.96);
    layer.container.alpha = 0;
    layer.animation = {
      kind: 'enter',
      elapsedMs: 0,
      durationMs: CHARACTER_ENTER_DURATION_MS,
      fromX: layer.targetX + slideOffset,
      toX: layer.targetX,
      fromY: layer.targetY + 18,
      toY: layer.targetY,
      fromAlpha: 0,
      toAlpha: 1,
      fromScale: 0.96,
      toScale: 1
    };
  }

  private startExpressionInAnimation(layer: CharacterLayer): void {
    layer.container.position.set(layer.targetX, layer.targetY);
    layer.container.scale.set(1);
    layer.container.alpha = 0;
    layer.animation = {
      kind: 'expressionIn',
      elapsedMs: 0,
      durationMs: CHARACTER_EXPRESSION_CROSSFADE_MS,
      fromX: layer.targetX,
      toX: layer.targetX,
      fromY: layer.targetY,
      toY: layer.targetY,
      fromAlpha: 0,
      toAlpha: 1,
      fromScale: 1,
      toScale: 1
    };
  }

  private startExitAnimation(layer: CharacterLayer, mode: 'exit' | 'expression'): void {
    layer.exiting = true;
    const isExpressionChange = mode === 'expression';
    layer.animation = {
      kind: isExpressionChange ? 'expressionOut' : 'exit',
      elapsedMs: 0,
      durationMs: isExpressionChange
        ? CHARACTER_EXPRESSION_CROSSFADE_MS
        : CHARACTER_EXIT_DURATION_MS,
      fromX: layer.container.x,
      toX: layer.container.x,
      fromY: layer.container.y,
      toY: layer.container.y + (isExpressionChange ? 0 : 16),
      fromAlpha: layer.container.alpha,
      toAlpha: 0,
      fromScale: layer.container.scale.x,
      toScale: isExpressionChange ? layer.container.scale.x : 0.98
    };
  }

  private startMoveAnimation(layer: CharacterLayer): void {
    layer.animation = {
      kind: 'move',
      elapsedMs: 0,
      durationMs: CHARACTER_MOVE_DURATION_MS,
      fromX: layer.container.x,
      toX: layer.targetX,
      fromY: layer.container.y,
      toY: layer.targetY,
      fromAlpha: layer.container.alpha,
      toAlpha: 1,
      fromScale: layer.container.scale.x,
      toScale: 1
    };
  }

  private updateLayerAnimation(layer: CharacterLayer, deltaMs: number): void {
    const animation = layer.animation;
    if (!animation) {
      return;
    }

    animation.elapsedMs = Math.min(animation.durationMs, animation.elapsedMs + deltaMs);
    const progress =
      animation.durationMs <= 0 ? 1 : Math.min(1, animation.elapsedMs / animation.durationMs);
    const easedProgress =
      animation.kind === 'enter'
        ? easeOutBack(progress)
        : animation.kind === 'exit' || animation.kind === 'expressionOut'
          ? easeInCubic(progress)
          : easeOutCubic(progress);
    const alphaProgress =
      animation.kind === 'enter' || animation.kind === 'expressionIn'
        ? easeOutCubic(progress)
        : easedProgress;

    layer.container.position.set(
      lerp(animation.fromX, animation.toX, easedProgress),
      lerp(animation.fromY, animation.toY, easedProgress)
    );
    layer.container.alpha = lerp(animation.fromAlpha, animation.toAlpha, alphaProgress);
    layer.container.scale.set(lerp(animation.fromScale, animation.toScale, easedProgress));

    if (progress < 1) {
      return;
    }

    layer.container.position.set(animation.toX, animation.toY);
    layer.container.alpha = animation.toAlpha;
    layer.container.scale.set(animation.toScale);
    layer.animation = null;
  }
}

function resolveExpression(
  definition: CharacterDefinition | undefined,
  requestedExpression: string | undefined
): CharacterExpressionDefinition {
  const expressions = definition?.expressions ?? [];
  const expressionId = requestedExpression ?? definition?.defaultExpression ?? 'normal';
  return (
    expressions.find((expression) => expression.id === expressionId) ??
    expressions.find((expression) => expression.id === definition?.defaultExpression) ?? {
      id: expressionId,
      label: expressionId,
      fileName: `${definition?.id ?? 'unknown'}.webp`
    }
  );
}

function getCharacterAssetReference(
  characterId: string,
  expression: CharacterExpressionDefinition
): CharacterAssetReference {
  const normalizedAssetPath = normalizeCharacterAssetPath(expression.assetPath);
  const fileName =
    expression.fileName ?? getFileNameFromPath(normalizedAssetPath) ?? `${characterId}.webp`;
  const id = fileName ? stripImageExtension(fileName) : characterId;
  const assetPath = expression.assetPath ?? `${CHARACTER_ASSET_PREFIX}${fileName}`;

  if (!normalizedAssetPath || isManifestCharacterAssetPath(normalizedAssetPath)) {
    return { id, fileName, assetPath };
  }

  return {
    id,
    fileName,
    assetPath,
    path: normalizedAssetPath
  };
}

function normalizeCharacterAssetPath(assetPath: string | undefined): string | undefined {
  if (!assetPath) {
    return undefined;
  }

  return assetPath;
}

function isManifestCharacterAssetPath(assetPath: string): boolean {
  return assetPath.startsWith(CHARACTER_ASSET_PREFIX);
}

function getFileNameFromPath(path: string | undefined): string | undefined {
  return path?.split('/').at(-1);
}

function stripImageExtension(fileName: string): string {
  return fileName.replace(/\.(png|webp|jpe?g)$/i, '');
}

function cloneCue(cue: ScenarioCharacterCue): ScenarioCharacterCue {
  return { ...cue };
}

function createLayerKey(characterId: string, expressionId: string): string {
  return `${characterId}:${expressionId}`;
}

function getEnterSlideOffset(position: CharacterPosition): number {
  switch (position) {
    case 'left':
      return -CHARACTER_SLIDE_OFFSET;
    case 'right':
      return CHARACTER_SLIDE_OFFSET;
    case 'center':
      return 0;
  }
}

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}

function easeInCubic(progress: number): number {
  return progress * progress * progress;
}

function easeOutBack(progress: number): number {
  const c1 = 1.2;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(progress - 1, 3) + c1 * Math.pow(progress - 1, 2);
}

function fitSprite(sprite: Sprite, targetWidth: number, targetHeight: number): void {
  const textureWidth = Math.max(1, sprite.texture.width);
  const textureHeight = Math.max(1, sprite.texture.height);
  const scale = Math.min(targetWidth / textureWidth, targetHeight / textureHeight);
  sprite.scale.set(scale);
}

function destroyLayers(layers: CharacterLayer[]): void {
  for (const layer of layers.splice(0)) {
    layer.container.destroy(DISPLAY_DESTROY_OPTIONS);
  }
}

function getRoleAccent(role: CharacterDefinition['role'] | undefined): number {
  switch (role) {
    case 'detective':
    case 'rival':
      return UI_COLORS.gold;
    case 'heroine':
    case 'support':
      return UI_COLORS.red;
    case 'police':
      return 0x6b8aa6;
    case 'phantom_thief':
      return 0x9f79ff;
    case 'victim':
      return 0xb86a87;
    case 'protagonist':
      return 0x8fbf9f;
    default:
      return UI_COLORS.mutedInk;
  }
}
